// -------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -------------------------------------------------------------

import * as React from 'react';
import { initializeIcons } from 'office-ui-fabric-react';
import { InstantiateGlobalAnnounce } from 'react-screen-reader-announce';
import { Provider, useSelector } from 'react-redux';
import { Redirect, Route, Router, Switch } from 'react-router-dom';

import CorrelationVector from './lib/CorrelationVector';
import { logException } from './lib/ApplicationInsights';
import ErrorBoundaryContext from './lib/ErrorBoundaryContext';
import ErrorPage from './ErrorPage';
import ErrorBoundary from './ErrorBoundary';
import history from './history';
import store from './store';
import allRoutes, { IRoute } from './views';
import HiddenAnnouncement from './components/HiddenAnnouncement';
import { showBrowserWarning } from './helpers/browser';
import { MsalAuthenticationTemplate, MsalProvider, useAccount } from '@azure/msal-react';
import { InteractionType, IPublicClientApplication } from '@azure/msal-browser';
import { msalInstance } from 'src/index';
import { getUserInfoData } from './store/user/index';

initializeIcons(/* optional base url */);

function PrivateRoute({ component: Component, ...rest }: { component: any }) {
    const accounts = msalInstance.getAllAccounts();
    const account = useAccount(accounts[0] || {});
    if (account && account.name) {
        const userInfo = useSelector((state: any) => state.userInfo);
        if (!userInfo || !userInfo.isValid) {
            store.dispatch<any>(getUserInfoData());
        }
    }

    const userInfo = useSelector((state: any) => state.userInfo);
    const render = (props: any): React.ReactNode => {
        if (userInfo && userInfo.isValid) {
            return <Component {...props} />;
        }
        return <Redirect to={{ pathname: '/authorize', state: { from: props.location } }} />;
    };
    return (
        <Route
            {...rest}
            render={render}
        />
    );
}

interface State {
    correlationVector?: string;
    error?: any;
    showBrowserWarningMessage: boolean;
}

type AppProps = {
    pca: IPublicClientApplication
};

class App extends React.Component<AppProps, State> {

    constructor(props: any) {
        super(props);
        if (history.location.pathname.toLowerCase().includes('authorize')) {
            history.push('/');
        }

        this.state = {
            correlationVector: undefined,
            error: undefined,
            showBrowserWarningMessage: showBrowserWarning(),
        };
    }

    componentDidCatch(error?: any, errorInfo?: any) {
        const correlationVector = CorrelationVector.getCV();
        logException(correlationVector, error, errorInfo, false);
        this.setState({
            correlationVector,
            error,
        });
    }

    onDismissBrowserWarningMessage = () => {
        this.setState({
            showBrowserWarningMessage: false,
        });
    }

    getRoutes(): JSX.Element[] {
        return allRoutes.map((route: IRoute) => {
            return route.public === true
                ? <Route key={route.path} {...route} />
                : <PrivateRoute key={route.path} {...route} />;
        });
    }

    render(): React.ReactNode {
        const { pca } = this.props;
        return (
            <Provider store={store}>
                <React.Suspense fallback="loading">
                    <HiddenAnnouncement />
                    <InstantiateGlobalAnnounce />
                    {
                        this.state.error ? (
                            <ErrorBoundaryContext.Provider value={{ ...this.state, onDismissBrowserWarningMessage: this.onDismissBrowserWarningMessage }}>
                                <Router history={history}>
                                    <ErrorPage />
                                </Router>
                            </ErrorBoundaryContext.Provider>
                        ) : (
                            <ErrorBoundary
                                onDismissBrowserWarningMessage={this.onDismissBrowserWarningMessage}
                                showBrowserWarningMessage={this.state.showBrowserWarningMessage}
                            >
                                <MsalProvider instance={pca}>
                                    <MsalAuthenticationTemplate
                                        interactionType={InteractionType.Redirect}
                                        errorComponent={ErrorPage}
                                    >
                                        <Router history={history}>
                                            <Switch>
                                                {this.getRoutes()}
                                                <Redirect key="404" from="*" to="/" />
                                            </Switch>
                                        </Router>
                                    </MsalAuthenticationTemplate>
                                </MsalProvider>
                            </ErrorBoundary>
                        )
                    }
                </React.Suspense>
            </Provider>
        );
    }
}

export default App;